import type {
  VeraGroundingCitation,
  VeraGroundingSource,
} from '../models/VeraGoogleSearchGrounding';

export type GroundedCitationRef = {
  citation: VeraGroundingCitation;
  displayNumbers: number[];
};

export type GroundedMessageSegment =
  | { type: 'markdown'; content: string }
  | { type: 'lineWithCitations'; content: string; citations: GroundedCitationRef[] }
  | { type: 'trailingCitations'; citations: GroundedCitationRef[] };

function buildIndexToDisplay(sources: VeraGroundingSource[]): Map<number, number> {
  const indexToDisplay = new Map<number, number>();
  sources.forEach((source, idx) => indexToDisplay.set(source.index, idx + 1));
  return indexToDisplay;
}

function toCitationRef(
  citation: VeraGroundingCitation,
  indexToDisplay: Map<number, number>,
): GroundedCitationRef | null {
  const displayNumbers = citation.sourceIndices
    .map((idx) => indexToDisplay.get(idx))
    .filter((n): n is number => n !== undefined)
    .sort((a, b) => a - b);
  if (displayNumbers.length === 0) return null;
  return { citation, displayNumbers };
}

function indexCitationsByLine(
  lines: string[],
  citations: VeraGroundingCitation[],
  indexToDisplay: Map<number, number>,
): Map<number, GroundedCitationRef[]> {
  const citationsByLine = new Map<number, GroundedCitationRef[]>();
  let offset = 0;

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const line = lines[lineIndex];
    const lineStart = offset;
    const lineEnd = offset + line.length;

    for (const citation of citations) {
      const ref = toCitationRef(citation, indexToDisplay);
      if (!ref) continue;
      if (citation.endIndex > lineStart && citation.endIndex <= lineEnd + 1) {
        const existing = citationsByLine.get(lineIndex) ?? [];
        existing.push(ref);
        citationsByLine.set(lineIndex, existing);
      }
    }

    offset = lineEnd + 1;
  }

  return citationsByLine;
}

function buildTrailingCitationSegments(
  text: string,
  citations: VeraGroundingCitation[],
  indexToDisplay: Map<number, number>,
): GroundedMessageSegment[] | null {
  const allRefs = citations
    .map((citation) => toCitationRef(citation, indexToDisplay))
    .filter((ref): ref is GroundedCitationRef => !!ref)
    .sort((a, b) => a.citation.startIndex - b.citation.startIndex);
  if (allRefs.length === 0) {
    return null;
  }
  return [
    { type: 'markdown', content: text },
    { type: 'trailingCitations', citations: allRefs },
  ];
}

/**
 * Splits assistant text into markdown blocks and per-line rows with citation chips.
 * Lines with citations render markdown for the line text plus chips as siblings (not raw spans).
 */
export function buildGroundedMessageSegments(
  text: string,
  citations: VeraGroundingCitation[],
  sources: VeraGroundingSource[],
): GroundedMessageSegment[] {
  const indexToDisplay = buildIndexToDisplay(sources);
  const lines = text.split('\n');
  const citationsByLine = indexCitationsByLine(lines, citations, indexToDisplay);

  const segments: GroundedMessageSegment[] = [];
  const markdownBuffer: string[] = [];

  const flushMarkdown = () => {
    if (markdownBuffer.length === 0) return;
    segments.push({ type: 'markdown', content: markdownBuffer.join('\n') });
    markdownBuffer.length = 0;
  };

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const lineCitations = citationsByLine.get(lineIndex);
    if (lineCitations?.length) {
      flushMarkdown();
      const sorted = [...lineCitations].sort(
        (a, b) => a.citation.startIndex - b.citation.startIndex,
      );
      segments.push({ type: 'lineWithCitations', content: lines[lineIndex], citations: sorted });
    } else {
      markdownBuffer.push(lines[lineIndex]);
    }
  }

  flushMarkdown();

  const matchedCitationCount = [...citationsByLine.values()].reduce(
    (sum, refs) => sum + refs.length,
    0,
  );
  if (matchedCitationCount === 0 && citations.length > 0) {
    return buildTrailingCitationSegments(text, citations, indexToDisplay) ?? segments;
  }

  return segments;
}
